<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import BusinessHoursTimePicker from './BusinessHoursTimePicker.vue';
import {
  defaultTimeSlot,
  timeSlotParse,
  timeSlotTransform,
  timeZoneOptions,
} from 'dashboard/routes/dashboard/settings/inbox/helpers/businessHour';

const props = defineProps({
  value: {
    type: Object,
    required: true,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
  showResetAction: {
    type: Boolean,
    default: false,
  },
  referenceText: {
    type: String,
    default: '',
  },
  saveLabel: {
    type: String,
    default: '',
  },
  resetLabel: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['save', 'reset', 'update:enabled']);
const { t } = useI18n();

const enabled = ref(false);
const timezone = ref('UTC');
const schedule = ref([]);
const initialSnapshot = ref('');

const timezoneChoices = computed(() => timeZoneOptions());

const shortDayLabels = computed(() => [
  'Sun',
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
]);

const defaultSchedule = () =>
  defaultTimeSlot.map(slot => ({
    ...slot,
    openAllDay: false,
  }));

const payloadFromState = () => {
  const normalizedSchedule = schedule.value.map(slot => {
    if (!slot.valid) {
      return {
        ...slot,
        openAllDay: false,
        from: '',
        to: '',
      };
    }

    return slot;
  });

  return {
    working_hours_enabled: enabled.value,
    timezone: timezone.value || 'UTC',
    working_hours: timeSlotTransform(normalizedSchedule),
  };
};

const invalidRows = computed(() =>
  schedule.value.some(slot => {
    if (!slot.valid || slot.openAllDay) return false;
    return !slot.from || !slot.to || slot.from === slot.to;
  })
);

const hasChanges = computed(
  () => JSON.stringify(payloadFromState()) !== initialSnapshot.value
);

const canSave = computed(() => !invalidRows.value && hasChanges.value);

const syncFromValue = value => {
  enabled.value = Boolean(value?.working_hours_enabled);
  timezone.value = value?.timezone || 'UTC';
  schedule.value = value?.working_hours?.length
    ? timeSlotParse(value.working_hours)
    : defaultSchedule();
  initialSnapshot.value = JSON.stringify(payloadFromState());
};

watch(
  () => props.value,
  value => syncFromValue(value),
  { deep: true, immediate: true }
);

watch(enabled, value => {
  emit('update:enabled', value);
});

const onSave = () => {
  emit('save', payloadFromState());
};
</script>

<template>
  <div class="flex w-full min-w-0 flex-col gap-6">
    <div
      v-if="referenceText || showResetAction"
      class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-surface-2 px-4 py-4 md:flex-row md:items-center md:justify-between"
    >
      <p v-if="referenceText" class="mb-0 text-sm text-n-slate-11">
        {{ referenceText }}
      </p>
      <Button
        v-if="showResetAction"
        sm
        slate
        outline
        :label="resetLabel || t('BUSINESS_HOURS.RESET')"
        @click="$emit('reset')"
      />
    </div>

    <div
      class="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,20rem)]"
    >
      <div class="flex items-center gap-3">
        <Switch v-model="enabled" size="md" />
        <div class="flex flex-col">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('BUSINESS_HOURS.ENABLE.TITLE') }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{ t('BUSINESS_HOURS.ENABLE.DESCRIPTION') }}
          </span>
        </div>
      </div>

      <div class="min-w-0">
        <label class="mb-2 block text-label-small font-medium text-n-slate-11">
          {{ t('BUSINESS_HOURS.TIMEZONE') }}
        </label>
        <SelectInput
          v-model="timezone"
          :options="timezoneChoices"
          full-width
          :placeholder="t('BUSINESS_HOURS.TIMEZONE_PLACEHOLDER')"
        />
      </div>
    </div>

    <div
      v-if="enabled"
      class="rounded-2xl border border-n-weak bg-n-surface-1 px-5 py-5"
    >
      <div class="mb-4 grid grid-cols-[minmax(0,8rem)_minmax(0,1fr)] gap-4">
        <div
          class="flex items-center gap-2 text-sm font-medium text-n-slate-11"
        >
          <span
            class="flex size-5 items-center justify-center rounded-md border border-n-weak"
          >
            <i class="i-lucide-calendar-days size-3.5" />
          </span>
          <span>{{ t('BUSINESS_HOURS.TABLE.DAY') }}</span>
        </div>
        <div
          class="flex items-center gap-2 text-sm font-medium text-n-slate-11"
        >
          <i class="i-lucide-clock-3 size-4" />
          <span>
            {{ t('BUSINESS_HOURS.TABLE.FROM') }}
            {{ t('BUSINESS_HOURS.TABLE.TO') }}
          </span>
        </div>
      </div>

      <div class="flex flex-col gap-3">
        <div
          v-for="slot in schedule"
          :key="slot.day"
          class="grid grid-cols-[minmax(0,8rem)_minmax(0,1fr)] items-center gap-4"
        >
          <label class="mb-0 flex items-center gap-3">
            <input
              v-model="slot.valid"
              type="checkbox"
              class="size-4 rounded border border-n-weak accent-[var(--color-n-brand)]"
            />
            <span class="text-sm font-medium text-n-slate-12">
              {{ shortDayLabels[slot.day] }}
            </span>
          </label>

          <div class="flex flex-wrap items-center gap-3">
            <template v-if="!slot.openAllDay">
              <div class="min-w-0 max-w-[10rem] flex-1 basis-[9rem]">
                <BusinessHoursTimePicker
                  v-model="slot.from"
                  :disabled="!slot.valid"
                  :has-error="
                    slot.valid && !!slot.from && slot.from === slot.to
                  "
                  :placeholder="t('BUSINESS_HOURS.SELECT_TIME')"
                />
              </div>

              <i class="i-lucide-clock-3 size-4 shrink-0 text-n-slate-11" />

              <span class="text-sm text-n-slate-11">
                {{ t('BUSINESS_HOURS.TABLE.TO').toLowerCase() }}
              </span>

              <div class="min-w-0 max-w-[10rem] flex-1 basis-[9rem]">
                <BusinessHoursTimePicker
                  v-model="slot.to"
                  :disabled="!slot.valid"
                  :has-error="slot.valid && !!slot.to && slot.from === slot.to"
                  :placeholder="t('BUSINESS_HOURS.SELECT_TIME')"
                />
              </div>

              <i class="i-lucide-clock-3 size-4 shrink-0 text-n-slate-11" />
            </template>

            <label class="mb-0 flex items-center gap-2 text-sm text-n-slate-11">
              <input
                v-model="slot.openAllDay"
                type="checkbox"
                :disabled="!slot.valid"
                class="size-4 rounded border border-n-weak accent-[var(--color-n-brand)]"
              />
              {{ t('BUSINESS_HOURS.ALL_DAY') }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <p v-if="invalidRows" class="mb-0 text-sm text-n-ruby-9">
      {{ t('BUSINESS_HOURS.VALIDATION') }}
    </p>

    <div class="flex justify-start">
      <Button
        sm
        color="blue"
        :label="saveLabel || t('BUSINESS_HOURS.SAVE')"
        :disabled="!canSave"
        :is-loading="isSaving"
        @click="onSave"
      />
    </div>
  </div>
</template>
